"use client";
import { Box, Button, Stack } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
// import SortableItem, { ALBUM_RULES } from "./SortableItem";
import { AppModal } from "@/common";
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  DragOverlay,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { UploadIcon } from "@/icons";
import { UseFormSetValue, useForm } from "react-hook-form";
import { useHandleUploadImage } from "@/hooks";
import { useDropzone } from "react-dropzone";
import { AppConstant, DataConstant } from "@/const";
import {
  CategoryTypeUploadEnum,
  DefaultModalGalaxyType,
  IUploadFile,
  SizeModalEnum,
} from "@/models";
import AppUploadFileDropzone from "./AppUploadFileDropzone";
import SortableItem from "./SortableItem";
import AppButton from "../../AppButton";

const ModalImageList = <T extends DefaultModalGalaxyType>({
  name,
  data,
  open,
  maxLengthTitle = 200,
  onClose,
  onConvertModel,
  onSetValueForm,
  fieldDefaultGalaxy,
  categoryType,
  ownerId = "",
}: ModalImageListProps<T>) => {
  const [activeId, setActiveId] = useState<string | number | null>(null);
  const [galaxyTemporary, setGalaxyTemporary] = useState<Array<T>>([...data]);
  const [isLoading, setIsLoading] = useState(false);
  const handleUploadImages = useHandleUploadImage();
  const { getRootProps, getInputProps } = useDropzone({
    accept: AppConstant.ACCEPT_IMAGE,
    onDrop: (acceptedFiles) => {
      handleUploadImageAlbum(acceptedFiles);
    },
  });

  const [isError, setIsError] = useState(false);

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const handleUploadImageAlbum = useCallback(
    async (acceptedFiles: File[]) => {
      setIsLoading(true);
      const fileArr = await handleUploadImages(
        acceptedFiles,
        categoryType,
        ownerId
      );
      setIsLoading(false);
      if (fileArr.length) {
        const newConvertFileArr = onConvertModel(fileArr);

        setGalaxyTemporary([...galaxyTemporary, ...newConvertFileArr]);
      }
    },
    [onSetValueForm, galaxyTemporary]
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (over && active.id !== over?.id) {
      setGalaxyTemporary((data) => {
        const oldIndex = data.findIndex((item) => item.id === active.id);
        const newIndex = data.findIndex((item) => item.id === over.id);
        return arrayMove(data, oldIndex, newIndex);
      });
    }
  };

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  const handleSubmitData = async () => {
    const hasError = galaxyTemporary.some((item) => {
      return item.name && item.name?.length > maxLengthTitle;
    });
    if (hasError) {
      setIsError(true);
      return;
    } else {
      setIsError(false);
      if (fieldDefaultGalaxy) {
        const albumThumb = galaxyTemporary.filter((item) =>
          Boolean(item.isDefault)
        );
        if (!albumThumb.length) {
          onSetValueForm(fieldDefaultGalaxy, "");
          // onSetValueForm(fieldDefaultGalaxy, albumThumb[0]?.id);
        }
        // else {
        //   // onSetValueForm(
        //   //   fieldDefaultGalaxy,
        //   //   galaxyTemporary.length > 0 ? galaxyTemporary[0]?.id : ""
        //   // );
        //   // galaxyTemporary[0].isDefault = true;

        // }
      }
      onSetValueForm(name, galaxyTemporary);
      onClose();
    }
  };

  const handleModalClose = () => {
    setGalaxyTemporary(data);
    onClose();
  };

  const handleEditTitle = (index: number, newTitle: string) => {
    setGalaxyTemporary((prevData) => {
      const newData = [...prevData];
      newData[index] = { ...newData[index], name: newTitle };

      const hasError = newData.some(
        (item) => item.name && item.name?.length > maxLengthTitle
      );
      setIsError(hasError);
      return newData;
    });
  };

  const handleDeleteImage = (index: number) => {
    const newData = galaxyTemporary.filter(
      (_, imageIndex) => imageIndex !== index
    );
    setGalaxyTemporary(newData);
  };

  useEffect(() => {
    setGalaxyTemporary([...data]);
  }, [data]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <AppModal
        open={open}
        onClose={handleModalClose}
        header={"Tệp tin của bạn"}
        size={SizeModalEnum.Lg}
        footer={
          <Stack direction="row" spacing={2}>
            {isLoading ? (
              <AppButton
                color="secondary"
                variant="outlined"
                startIcon={<UploadIcon />}
                disabled={isLoading}
                size={"medium"}
              >
                Đang tải ảnh ...
              </AppButton>
            ) : (
              <AppUploadFileDropzone
                inputProp={getInputProps()}
                rootProp={getRootProps()}
              >
                <AppButton
                  color="secondary"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  size={"medium"}
                >
                  Thêm ảnh
                </AppButton>
              </AppUploadFileDropzone>
            )}
            <AppButton
              variant="contained"
              onClick={handleSubmitData}
              disabled={isError}
              size={"medium"}
            >
              Xong
            </AppButton>
          </Stack>
        }
        content={
          <SortableContext
            items={galaxyTemporary}
            strategy={rectSortingStrategy}
          >
            <Box className="grid grid-cols-2   gap-5">
              {galaxyTemporary?.map((item, index) => (
                <SortableItem
                  key={item.id}
                  data={item}
                  id={item?.id}
                  index={index}
                  onEditTitle={handleEditTitle}
                  onDeleteImage={() => handleDeleteImage(index)}
                />
              ))}
              <DragOverlay adjustScale style={{ transformOrigin: "0 0 " }}>
                {activeId ? (
                  <SortableItem
                    onEditTitle={handleEditTitle}
                    id={activeId}
                    data={
                      galaxyTemporary?.filter(
                        (item) => item.id === activeId
                      )?.[0]
                    }
                  />
                ) : null}
              </DragOverlay>
            </Box>
          </SortableContext>
        }
      />
    </DndContext>
  );
};

export default ModalImageList;
export type ModalImageListProps<T> = {
  name: string;
  maxLengthTitle?: number;
  data: T[];
  open: boolean;
  onClose: () => void;
  onSetValueForm: UseFormSetValue<any>;
  onConvertModel: (dataConvert: IUploadFile[]) => T[]; // convert model upload file sang modal cần lưu
  fieldDefaultGalaxy?: string;
  categoryType: CategoryTypeUploadEnum;
  ownerId?: string;
};

// fileArr.map((item, index) => {
//   return {
//     id: `preview_${item.id}`,
//     title: ``,
//     imageUrl: item.url,
//     imageType: item.fileType,
//     attachmentId: item.id,
//     imageSize: item.size,
//     order: DataConstant.DEFAULT_ORDER,
//     isThumbAlbum: index === 0 ? true : false,
//   };
// });
